/**
 * Build a self-contained JSON "brief" for a failure mode — its definition plus a
 * handful of full example traces — designed to be copied out of the Failure
 * Clusters detail panel and pasted straight into a coding agent (Claude Code or
 * similar) to drive a fix (issue #1324).
 *
 * This closes, in one paste, the loop the clustering design doc deliberately
 * left manual (015 §2 — "export traces" was out of scope for v1): production
 * failure → named failure mode → reproducing examples → fix + regression test.
 *
 * Pure + deterministic (inject `generatedAt` in tests), so the brief's *shape*
 * is unit-tested directly here, separate from the fetch/clipboard plumbing in
 * `hooks/useAgentBriefCopy.ts`.
 */

import type {
  FailureMode,
  FailurePoint,
  Granularity,
  ModeStatus,
  TraceDetail,
} from "../types";
import { modeTrend } from "./trend";
import { windowNoun } from "./windowCopy";

/**
 * Max full-content example traces embedded in a brief: enough to show the
 * pattern, few enough to stay paste-able (a single trace can be tens of KB).
 */
export const AGENT_BRIEF_MAX_EXAMPLES = 5;

// What the agent should do with the brief. Leads the JSON so it is the first
// thing the model reads, and names the fields it will find below.
const INSTRUCTIONS =
  "This is a recurring failure mode that Composo detected in the production " +
  "traces of an LLM application. Use it to improve the app: (1) study the " +
  "failure mode and the example traces below — each shows the model's " +
  "input/output (`trace`), Composo's evaluation results (`evaluations`), and " +
  "why it was judged a failure (`why_it_failed`); (2) find the root cause in " +
  "this codebase (e.g. the system prompt, tool definitions, routing, output " +
  "parsing, or retrieval); (3) propose a concrete fix; (4) add a regression " +
  "test that reproduces this failure mode so it cannot silently return. The " +
  "occurrence counts are over the window currently selected in Composo.";

const SOURCE = "Composo · Failure Clusters";

/** One example as handed to the builder: the in-window point plus the trace's
 * full content (or null when it could not be fetched — deleted / ZDR). */
export interface AgentBriefExampleInput {
  point: FailurePoint;
  trace: TraceDetail | null;
}

export interface BuildAgentBriefParams {
  mode: FailureMode;
  granularity: Granularity;
  /** Live "now" view vs a scrubbed historical window — recorded in the brief. */
  isLive: boolean;
  examples: AgentBriefExampleInput[];
  /** Injectable for deterministic tests; defaults to now. */
  generatedAt?: Date;
}

interface AgentBriefEvaluation {
  criteria: string | null;
  score: number | null;
  explanation: string | null;
  evaluator_name: string | null;
}

interface AgentBriefExample {
  trace_id: string;
  occurred_at: string;
  why_it_failed: string;
  trace: Record<string, unknown> | null;
  evaluations: AgentBriefEvaluation[];
  tags: Record<string, string> | null;
}

export interface AgentBrief {
  instructions: string;
  failure_mode: {
    label: string;
    description: string | null;
    occurrences_in_window: number;
    occurrences_in_previous_window: number;
    trend: string;
    status: ModeStatus;
    first_seen: string;
    window: { granularity: Granularity; view: "live" | "historical" };
  };
  example_traces: AgentBriefExample[];
  generated_at: string;
  source: string;
}

/** Human-readable window-over-window trend, reusing the legend's tested math. */
function trendSummary(mode: FailureMode, granularity: Granularity): string {
  const { delta, direction, pct } = modeTrend(
    mode.size_in_window,
    mode.prev_window_size,
  );
  const noun = windowNoun(granularity);
  if (direction === "flat") return `no change vs the previous ${noun}`;
  const sign = delta > 0 ? "+" : "";
  const pctText = pct === null ? "" : ` (${sign}${pct}%)`;
  return `${sign}${delta}${pctText} vs the previous ${noun}`;
}

function toEvaluations(trace: TraceDetail | null): AgentBriefEvaluation[] {
  if (!trace?.evaluations) return [];
  return trace.evaluations.map((e) => ({
    criteria: e.criteria,
    score: e.score,
    explanation: e.explanation,
    evaluator_name: e.evaluator_name,
  }));
}

/** Assemble the brief. Pure: same inputs → same JSON (given `generatedAt`). */
export function buildAgentBrief({
  mode,
  granularity,
  isLive,
  examples,
  generatedAt = new Date(),
}: BuildAgentBriefParams): AgentBrief {
  return {
    instructions: INSTRUCTIONS,
    failure_mode: {
      label: mode.label,
      description: mode.description,
      occurrences_in_window: mode.size_in_window,
      occurrences_in_previous_window: mode.prev_window_size,
      trend: trendSummary(mode, granularity),
      status: mode.status,
      first_seen: mode.first_seen_at,
      window: { granularity, view: isLive ? "live" : "historical" },
    },
    example_traces: examples.map(({ point, trace }) => ({
      trace_id: point.trace_id,
      occurred_at: point.created_at,
      why_it_failed: point.explanation,
      trace: trace?.trace ?? null,
      evaluations: toEvaluations(trace),
      tags: trace?.tags ?? null,
    })),
    generated_at: generatedAt.toISOString(),
    source: SOURCE,
  };
}
